import { useState, useEffect, useRef } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Fuel, 
  Plus, 
  Calendar,
  Euro,
  Car,
  FileImage,
  Trash2,
  Edit,
  Eye,
  Loader2,
  TrendingUp,
  ArrowLeft,
  Camera,
  Upload,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';


export default function DriverPetrol() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [plates, setPlates] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    kilometers: '',
    vehicle_id: user?.vehicle_id || '',
    notes: '',
    receipt_data: null,
    receipt_filename: null
  });

  useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchPlates();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/petrol`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching petrol entries:', error);
      toast.error('Failed to load petrol entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/petrol/summary/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPlates = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/plates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlates(res.data || []);
    } catch (e) {
      console.error('Error fetching plates:', e);
    }
  };

  const compressImage = (file, { maxWidth = 1600, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const scale = img.width > maxWidth ? maxWidth / img.width : 1;
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Iteratively shrink until under maxKB (base64 is ~1.37x binary size)
  const compressUntilFits = async (file, maxKB = 900) => {
    const attempts = [
      { maxWidth: 1600, quality: 0.72 },
      { maxWidth: 1400, quality: 0.62 },
      { maxWidth: 1200, quality: 0.55 },
      { maxWidth: 1000, quality: 0.45 },
      { maxWidth: 800, quality: 0.4 },
    ];
    let lastUrl = null;
    for (const opts of attempts) {
      lastUrl = await compressImage(file, opts);
      const sizeKB = Math.ceil((lastUrl.length * 3) / 4 / 1024);
      if (sizeKB <= maxKB) {
        return { dataUrl: lastUrl, sizeKB };
      }
    }
    // Return the smallest we got even if still over budget
    return { dataUrl: lastUrl, sizeKB: Math.ceil((lastUrl.length * 3) / 4 / 1024) };
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be less than 20MB');
      return;
    }

    try {
      let dataUrl;
      let filename = file.name;
      if (file.type.startsWith('image/')) {
        // Compress heavy phone photos aggressively — origin/Cloudflare choke on big base64
        const { dataUrl: url, sizeKB } = await compressUntilFits(file, 900);
        dataUrl = url;
        if (sizeKB > 1500) {
          toast.error('Photo is too complex to compress — try a clearer shot');
          return;
        }
        if (!filename.toLowerCase().endsWith('.jpg') && !filename.toLowerCase().endsWith('.jpeg')) {
          filename = filename.replace(/\.[^.]+$/, '') + '.jpg';
        }
      } else if (file.type === 'application/pdf') {
        if (file.size > 2 * 1024 * 1024) {
          toast.error('PDF must be less than 2MB');
          return;
        }
        dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
      } else {
        toast.error('Only images and PDF files are accepted');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        receipt_data: dataUrl,
        receipt_filename: filename,
      }));
    } catch (err) {
      console.error('Receipt read/compress error:', err);
      toast.error('Failed to process the receipt — try a different photo');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      kilometers: '',
      vehicle_id: user?.vehicle_id || '',
      notes: '',
      receipt_data: null,
      receipt_filename: null
    });
    setEditingEntry(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const submitPayload = async (payload) => {
    if (editingEntry) {
      return axios.put(`${API}/petrol/${editingEntry.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
    }
    return axios.post(`${API}/petrol`, payload, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 60000,
    });
  };

  const isCloudflareOrNetworkError = (error) => {
    if (!error) return false;
    // No response at all → network / Cloudflare 5xx / abort
    if (!error.response) return true;
    const st = error.response.status;
    // 502 Bad Gateway, 520/521/522/524 Cloudflare origin errors, 413 payload too large
    return st === 413 || st === 502 || st === 520 || st === 521 || st === 522 || st === 524;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.kilometers || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        date: new Date(formData.date).toISOString(),
        amount: parseFloat(formData.amount),
        kilometers: parseFloat(formData.kilometers),
        vehicle_id: formData.vehicle_id || null,
        notes: formData.notes || null,
        receipt_data: formData.receipt_data,
        receipt_filename: formData.receipt_filename
      };

      try {
        await submitPayload(payload);
        toast.success(editingEntry ? 'Petrol entry updated' : 'Petrol entry added');
      } catch (error) {
        // If the payload is too large / Cloudflare rejects, retry without receipt so
        // the entry is at least persisted. Driver can re-attach the photo afterwards.
        if (payload.receipt_data && isCloudflareOrNetworkError(error)) {
          console.warn('Petrol POST failed with payload — retrying without receipt', error);
          const stripped = { ...payload, receipt_data: null, receipt_filename: null };
          try {
            await submitPayload(stripped);
            toast.warning('Entry saved — but receipt photo failed to upload. Try re-attaching a smaller photo.');
          } catch (retryErr) {
            throw retryErr;
          }
        } else {
          throw error;
        }
      }

      setShowModal(false);
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error saving petrol entry:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to save entry — check your connection and try again';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (entry) => {
    // Fetch the receipt on-demand if not already inline (backend now strips it from list)
    let receiptData = entry.receipt_data || null;
    const hasStored = entry.has_receipt || entry.receipt_filename;
    if (!receiptData && hasStored) {
      try {
        const res = await axios.get(`${API}/petrol/${entry.id}/receipt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        receiptData = res.data?.receipt_data || null;
      } catch (err) {
        console.error('Fetch receipt for edit failed:', err);
      }
    }

    setEditingEntry(entry);
    setFormData({
      date: entry.date ? entry.date.slice(0, 10) : '',
      amount: entry.amount?.toString() || '',
      kilometers: entry.kilometers?.toString() || '',
      vehicle_id: entry.vehicle_id || '',
      notes: entry.notes || '',
      receipt_data: receiptData,
      receipt_filename: entry.receipt_filename
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/petrol/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const viewReceipt = async (entry) => {
    // If the receipt is already loaded (e.g. legacy list responses), just show it
    if (entry.receipt_data) {
      setSelectedReceipt(entry.receipt_data);
      setShowReceiptModal(true);
      return;
    }
    // Otherwise fetch on-demand from the dedicated endpoint
    if (!entry.has_receipt && !entry.receipt_filename) return;
    try {
      const res = await axios.get(`${API}/petrol/${entry.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.receipt_data) {
        setSelectedReceipt(res.data.receipt_data);
        setShowReceiptModal(true);
      } else {
        toast.error('Receipt not available');
      }
    } catch (err) {
      console.error('Fetch receipt error:', err);
      toast.error('Failed to load receipt');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-amber-600 text-white px-6 py-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/driver')}
            className="text-white hover:bg-amber-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Petrol Expenses</h1>
            <p className="text-amber-100 text-sm">Track your fuel expenses</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {/* Add Button */}
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="w-full mb-6 bg-amber-600 hover:bg-amber-700 py-6 text-lg"
          data-testid="add-petrol-btn"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Petrol Entry
        </Button>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Euro className="h-4 w-4 text-amber-600" />
                <p className="text-xs text-slate-500">Total Spent</p>
              </div>
              <p className="text-lg font-bold text-slate-900">€{stats.total_amount?.toLocaleString()}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Fuel className="h-4 w-4 text-blue-600" />
                <p className="text-xs text-slate-500">Entries</p>
              </div>
              <p className="text-lg font-bold text-slate-900">{stats.total_entries}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Car className="h-4 w-4 text-green-600" />
                <p className="text-xs text-slate-500">KM Tracked</p>
              </div>
              <p className="text-lg font-bold text-slate-900">{stats.total_km_tracked?.toLocaleString()}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-purple-600" />
                <p className="text-xs text-slate-500">€/km</p>
              </div>
              <p className="text-lg font-bold text-slate-900">€{stats.cost_per_km?.toFixed(3)}</p>
            </div>
          </div>
        )}

        {/* Entries List */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Your Entries</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <Fuel className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No petrol entries yet</p>
              <p className="text-slate-400 text-sm">Add your first entry above</p>
            </div>
          ) : (
            entries.map(entry => (
              <div 
                key={entry.id} 
                className="bg-white border border-slate-200 rounded-lg p-4"
                data-testid={`petrol-entry-${entry.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{formatDate(entry.date)}</span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Amount</p>
                        <p className="text-lg font-bold text-amber-600">€{entry.amount?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Kilometers</p>
                        <p className="text-lg font-semibold text-slate-700">{entry.kilometers?.toLocaleString()} km</p>
                      </div>
                      {entry.vehicle_plate && (
                        <div>
                          <p className="text-xs text-slate-500">Vehicle</p>
                          <p className="text-sm font-semibold text-slate-700">{entry.vehicle_plate}</p>
                        </div>
                      )}
                    </div>
                    
                    {entry.notes && (
                      <p className="text-sm text-slate-500 mt-2">{entry.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {(entry.has_receipt || entry.receipt_data || entry.receipt_filename) && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => viewReceipt(entry)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(entry)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(entry.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Petrol Entry' : 'Add Petrol Entry'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Vehicle</Label>
              <Select
                value={formData.vehicle_id || 'none'}
                onValueChange={(v) => setFormData({ ...formData, vehicle_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="mt-1" data-testid="driver-petrol-vehicle-select">
                  <SelectValue placeholder="Select vehicle (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No vehicle —</SelectItem>
                  {plates.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <Label>Kilometers *</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.kilometers}
                  onChange={(e) => setFormData({ ...formData, kilometers: e.target.value })}
                  placeholder="Current km"
                  className="mt-1"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Receipt (Photo)</Label>
              <div className="mt-1 space-y-3">
                {/* Two options: Take Photo or Upload File */}
                <div className="flex gap-2">
                  {/* Camera capture button */}
                  <div className="flex-1">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      capture="environment"
                      className="hidden"
                      id="petrol-camera-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      Take Photo
                    </Button>
                  </div>
                  
                  {/* File upload button */}
                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="petrol-file-upload-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('petrol-file-upload-input')?.click()}
                      className="w-full"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload File
                    </Button>
                  </div>
                </div>
              </div>
              {formData.receipt_filename && (
                <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                  <FileImage className="h-4 w-4" />
                  {formData.receipt_filename}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">Photos are compressed automatically before upload.</p>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., Gas station name..."
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingEntry ? 'Update' : 'Add Entry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl mx-4">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img 
                  src={selectedReceipt} 
                  alt="Receipt" 
                  className="max-h-[60vh] object-contain rounded-lg"
                />
              </div>
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedReceipt;
                    link.download = `petrol_receipt_${Date.now()}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Receipt downloaded');
                  }}
                  data-testid="download-petrol-receipt-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
